import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Chip, LoadingScreen } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDate, formatMoney, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { useAdminStore } from '../../lib/store';
import {
  Dialog,
  EmptyState,
  Field,
  FilterChips,
  ListCard,
  Page,
  Row,
  Table,
  Td,
  Th,
  fieldClass,
} from '../../ui';
import { toast } from '../../ui/toast';
import { Banner } from '@cowoo/shared/ui-web';
import { MemberPanel } from './MemberPanel';
import { CHIPS, memberName, statusOf, tariffOf, type MemberChip } from './memberRow';

/**
 * `/mitglieder` (10-web-admin § 10.1).
 *
 * The status column is the one thing on this screen that has to be right:
 * "Zahlung offen" and "gesperrt" are why somebody opens it. It is derived once
 * in `statusOf` and used by the list and the panel alike, so the two can never
 * describe the same person differently.
 */
export function Members() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const can = useAdminStore((state) => state.can);
  const [chip, setChip] = useState<MemberChip>('all');
  const [q, setQ] = useState('');
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  /** § 10.2 "Mitglied anlegen" — an invitation; the account appears at the first login. */
  const invite = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/members', {
        body: {
          email: inviteEmail.trim(),
          ...(inviteFirstName.trim() ? { firstName: inviteFirstName.trim() } : {}),
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setInviting(false);
      setInviteEmail('');
      setInviteFirstName('');
      setProblem(null);
      toast(t('admin.members.invited'), { tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('members') });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const members = useQuery({
    queryKey: qk.admin.area('members', { chip, q }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/members', {
        params: { query: { chip, ...(q ? { q } : {}) } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (members.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = members.data?.items ?? [];

  return (
    <Page>
      <ListCard
        title={t('admin.members.title')}
        actions={
          <>
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={t('admin.members.search')}
              className="border-line rounded-input text-adminBody h-9 w-[260px] border px-3"
            />
            {/* § 10.1 — pre-creating a member is owner work (`06-auth` § 4.2). */}
            {can('gdpr') ? (
              <Button size="sm" onClick={() => setInviting(true)}>
                {t('admin.members.create')}
              </Button>
            ) : null}
          </>
        }
      >
        <FilterChips
          value={chip}
          onChange={setChip}
          options={CHIPS.map((value) => ({
            value,
            label: t(`admin.members.chip.${value}`),
            count: members.data?.counts[value],
          }))}
        />

        {items.length === 0 ? (
          <EmptyState>{t('admin.members.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.members.column.name')}</Th>
                <Th>{t('admin.members.column.tariff')}</Th>
                <Th>{t('admin.members.column.account')}</Th>
                <Th>{t('admin.members.column.since')}</Th>
                <Th align="right">{t('admin.members.column.monthly')}</Th>
                <Th>{t('admin.members.column.status')}</Th>
              </>
            }
          >
            {items.map((member) => {
              const status = statusOf(member);
              return (
                <Row key={member.id} to={`/mitglieder/${member.id}`} selected={member.id === userId}>
                  <Td bold>{memberName(member)}</Td>
                  <Td>{tariffOf(member, t)}</Td>
                  <Td>{member.company ?? t('admin.members.private')}</Td>
                  <Td>{member.since ? formatDate(member.since) : '—'}</Td>
                  <Td numeric>
                    {member.monthlyGrossCents === null ? '—' : formatMoney(member.monthlyGrossCents)}
                  </Td>
                  <Td>
                    <Chip
                      tone={status.tone}
                      label={t(`admin.members.status.${status.key}`, status.values ?? {})}
                    />
                  </Td>
                </Row>
              );
            })}
          </Table>
        )}
      </ListCard>

      {userId ? <MemberPanel id={userId} onClose={() => navigate('/mitglieder')} /> : null}

      {inviting ? (
        <Dialog
          title={t('admin.members.inviteTitle')}
          intro={t('admin.members.inviteIntro')}
          confirmLabel={t('admin.members.inviteSubmit')}
          onConfirm={() => invite.mutate()}
          onClose={() => {
            setInviting(false);
            setProblem(null);
          }}
          busy={invite.isPending}
          disabled={!/.+@.+\..+/.test(inviteEmail.trim())}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <Field label={t('admin.members.inviteEmail')}>
            <input
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              className={fieldClass}
            />
          </Field>
          <Field label={t('admin.members.inviteFirstName')}>
            <input
              value={inviteFirstName}
              onChange={(event) => setInviteFirstName(event.target.value)}
              className={fieldClass}
            />
          </Field>
        </Dialog>
      ) : null}
    </Page>
  );
}
